import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import api from '../services/api'

const SESSION_NUMBER = 4

const QUESTIONS = {
  primaryHealthGoal: {
    label: 'Primary Health Goal',
    required: true,
    type: 'radio',
    options: ['Weight loss', 'Muscle gain', 'Improved Digestion'],
  },
  dietaryPreferences: {
    label: 'Dietary Preferences',
    required: true,
    type: 'radio',
    options: ['Veg', 'Non-Veg'],
  },
  outsideFoodConsumption: {
    label: 'Consumption of Outside Food',
    subtitle: 'Choose size and number per color',
    required: true,
    type: 'radio',
    options: ['Occasionally', 'Mostly', 'Rarely', 'Never'],
  },
  dailyEnergyLevels: {
    label: 'Daily Energy Levels',
    required: true,
    type: 'scale',
    min: 1,
    max: 10,
    lowLabel: 'Low',
    highLabel: 'High',
  },
  physicalActivity: {
    label: 'Physical Activity',
    required: true,
    type: 'radio',
    options: ['Occasionally', 'Rarely', 'Mostly', 'Never'],
  },
  healthCondition: {
    label: 'Health Condition',
    required: true,
    type: 'checkbox',
    options: ['PCOD/PCOS', 'Thyroid', 'BP', 'Diabetes', 'None', 'Others'],
    hasOthersText: true,
  },
  mealTimingConsistency: {
    label: 'On a scale of 1-10, how consistent are you currently with your meal timing?',
    required: false,
    type: 'scale',
    min: 1,
    max: 10,
    lowLabel: 'Very inconsistent',
    highLabel: 'Very consistent',
  },
  waterIntake: {
    label: 'How would you rate your current water intake per day?',
    required: false,
    type: 'radio',
    options: ['Below recommended amount', 'Adequate', 'Above recommended amount'],
  },
  queries: {
    label: 'Any specific questions regarding nutrition, sleep or energy optimization you\'d like to know more about',
    required: false,
    type: 'textarea',
  },
}

const QUESTION_ORDER = [
  'primaryHealthGoal',
  'dietaryPreferences',
  'outsideFoodConsumption',
  'dailyEnergyLevels',
  'physicalActivity',
  'healthCondition',
  'mealTimingConsistency',
  'waterIntake',
  'queries',
]

const RadioGroup = ({ options, value, onChange }) => (
  <div className="space-y-2">
    {options.map((option) => (
      <div key={option} onClick={() => onChange(option)} className="flex items-center gap-3 cursor-pointer group">
        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
          value === option ? 'border-purple-600 bg-purple-600' : 'border-gray-300 group-hover:border-purple-400'
        }`}>
          {value === option && <div className="w-2 h-2 rounded-full bg-white" />}
        </div>
        <span className="text-gray-700 text-sm">{option}</span>
      </div>
    ))}
  </div>
)

const CheckboxGroup = ({ options, value = [], onChange, hasOthersText, othersText = '', onOthersTextChange }) => (
  <div className="space-y-2">
    {options.map((option) => {
      const checked = value.includes(option)
      return (
        <div key={option}>
          <div onClick={() => onChange(option)} className="flex items-center gap-3 cursor-pointer group">
            <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
              checked ? 'border-purple-600 bg-purple-600' : 'border-gray-300 group-hover:border-purple-400'
            }`}>
              {checked && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-gray-700 text-sm">{option}</span>
          </div>
          {hasOthersText && option === 'Others' && checked && (
            <input
              type="text"
              value={othersText}
              onChange={(e) => onOthersTextChange?.(e.target.value)}
              placeholder="Please specify"
              className="mt-2 ml-8 w-[calc(100%-2rem)] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )
    })}
  </div>
)

const LinearScale = ({ min, max, value, onChange, lowLabel, highLabel }) => {
  const numbers = Array.from({ length: max - min + 1 }, (_, i) => min + i)
  return (
    <div>
      <div className="flex items-center justify-between gap-1 sm:gap-2">
        {numbers.map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full text-xs sm:text-sm font-medium transition-all ${
              value === num
                ? 'bg-purple-600 text-white shadow-lg scale-110'
                : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-700'
            }`}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  )
}

const Session4HealthFormModal = ({
  isOpen,
  onClose,
  programSlug,
  existingResponse,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      if (existingResponse?.formData) {
        setFormData(existingResponse.formData)
      } else {
        setFormData({})
      }
      setErrors({})
    }
  }, [isOpen, existingResponse])

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  const handleCheckboxChange = (key, option) => {
    const current = formData[key] || []
    const updated = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option]
    updateField(key, updated)
  }

  const validate = () => {
    const newErrors = {}
    for (const key of QUESTION_ORDER) {
      const q = QUESTIONS[key]
      if (!q.required) continue
      const val = formData[key]
      if (q.type === 'checkbox') {
        if (!val || val.length === 0) newErrors[key] = 'This field is required'
      } else if (q.type === 'scale') {
        if (val == null) newErrors[key] = 'Please select a value'
      } else if (!val) {
        newErrors[key] = 'This field is required'
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setIsSubmitting(true)
    try {
      await api.post(`/api/programs/${programSlug}/session-forms`, {
        sessionNumber: SESSION_NUMBER,
        formData,
      })
      onSuccess?.()
      onClose()
    } catch (err) {
      setErrors({ _general: err.response?.data?.message || 'Failed to submit form' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-10 px-3 sm:px-4 pb-4 overflow-y-auto"
      >
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.97 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Health Assessment</h2>
              <p className="text-xs text-gray-500">Session 4: Rest as Strategy</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
            {errors._general && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {errors._general}
              </div>
            )}

            {QUESTION_ORDER.map((key) => {
              const q = QUESTIONS[key]
              return (
                <div key={key} className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    {q.label}
                    {q.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {q.subtitle && (
                    <p className="text-xs text-gray-500 mb-2">{q.subtitle}</p>
                  )}

                  <div className="mt-2">
                    {q.type === 'radio' && (
                      <RadioGroup
                        options={q.options}
                        value={formData[key]}
                        onChange={(val) => updateField(key, val)}
                      />
                    )}
                    {q.type === 'checkbox' && (
                      <CheckboxGroup
                        options={q.options}
                        value={formData[key] || []}
                        onChange={(option) => handleCheckboxChange(key, option)}
                        hasOthersText={q.hasOthersText}
                        othersText={formData[`${key}_othersText`] || ''}
                        onOthersTextChange={(text) => updateField(`${key}_othersText`, text)}
                      />
                    )}
                    {q.type === 'scale' && (
                      <LinearScale
                        min={q.min}
                        max={q.max}
                        value={formData[key]}
                        onChange={(val) => updateField(key, val)}
                        lowLabel={q.lowLabel}
                        highLabel={q.highLabel}
                      />
                    )}
                    {q.type === 'textarea' && (
                      <textarea
                        value={formData[key] || ''}
                        onChange={(e) => updateField(key, e.target.value)}
                        placeholder="Your answer"
                        rows={3}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none"
                      />
                    )}
                  </div>

                  {errors[key] && (
                    <p className="text-xs text-red-600 mt-1">{errors[key]}</p>
                  )}
                </div>
              )
            })}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white rounded-b-2xl border-t border-gray-100 px-5 py-4">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : existingResponse ? 'Update Response' : 'Submit'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default Session4HealthFormModal
